import LegalLayout from './LegalLayout';

export default function DPA() {
  return (
    <LegalLayout
      title="Data Processing Addendum"
      subtitle="The DPA between Veridex (processor) and you as a B2B customer (controller), applying Art. 28 GDPR."
      lastUpdated="19 April 2026"
    >
      <h2>1. Scope</h2>
      <p>
        This Data Processing Addendum ("DPA") supplements the Veridex Terms of Service. It
        applies whenever Veridex processes personal data on behalf of the Customer in
        connection with the Service. In case of conflict with the Terms of Service, this DPA
        prevails for personal-data matters.
      </p>

      <h2>2. Roles</h2>
      <ul>
        <li><strong>Customer</strong> = data controller.</li>
        <li><strong>Veridex</strong> = data processor acting only on documented instructions from the Customer (namely, the use of the Service as configured).</li>
      </ul>

      <h2>3. Subject Matter and Duration</h2>
      <p>
        The subject matter is the provision of the Veridex Service to the Customer. Duration
        follows the lifetime of the Customer's subscription plus any post-termination
        retention required by law.
      </p>

      <h2>4. Categories of Data and Data Subjects</h2>
      <ul>
        <li><strong>Categories of data</strong>: account data, business operational data (invoices, supplier records, product data, CBAM declarations, emission factors), audit-trail metadata, usage telemetry.</li>
        <li><strong>Data subjects</strong>: Customer's employees and authorised users, Customer's suppliers (where identifiable), Customer's counterparties named on uploaded invoices.</li>
      </ul>

      <h2>5. Obligations of Veridex</h2>
      <ul>
        <li>Process personal data only on documented instructions from the Customer.</li>
        <li>Ensure all personnel authorised to process personal data are bound by confidentiality.</li>
        <li>Implement and maintain appropriate technical and organizational security measures (TLS in transit, encryption at rest, tenant isolation, access control, bcrypt password hashing, API-key rate limiting, change-log audit trail).</li>
        <li>Assist the Customer with data-subject requests and regulatory inquiries.</li>
        <li>Notify the Customer of any personal-data breach without undue delay and at the latest within 72 hours of becoming aware, together with the information required under Art. 33 GDPR.</li>
        <li>Delete or return all Customer personal data at the end of the contract, subject to statutory retention.</li>
        <li>Make available all information necessary to demonstrate compliance and allow for audits (reasonable frequency, written notice, confidentiality undertaking).</li>
      </ul>

      <h2>6. Sub-processors</h2>
      <p>
        The Customer grants general authorisation to Veridex to engage sub-processors. The
        current sub-processor list is published on the{' '}
        <a href="/legal/privacy">Privacy Policy</a> page and includes:
      </p>
      <ul>
        <li>Emergent (hosting and managed LLM)</li>
        <li>MongoDB (database)</li>
        <li>Stripe (billing)</li>
        <li>Resend (transactional email)</li>
        <li>Google Analytics 4 (analytics — cookie-gated)</li>
        <li>Sentry (error monitoring)</li>
        <li>HubSpot (CRM — cookie-gated)</li>
        <li>Tawk.to (live chat — cookie-gated)</li>
      </ul>
      <p>
        Veridex imposes equivalent data-protection obligations on all sub-processors and
        remains liable for their performance. Customers will be notified of any new
        sub-processor at least 30 days in advance and may object for legitimate reasons.
      </p>

      <h2>7. International Transfers</h2>
      <p>
        Where personal data is transferred outside the EU/EEA, Veridex relies on EU
        Standard Contractual Clauses (2021/914) and, where necessary, supplementary
        technical and organisational safeguards.
      </p>

      <h2>8. Assistance and Cooperation</h2>
      <p>
        Veridex assists the Customer in fulfilling its obligations under Arts. 32–36 GDPR,
        including data-protection impact assessments and prior consultation with
        supervisory authorities, taking into account the nature of processing and the
        information available.
      </p>

      <h2>9. Termination and Deletion</h2>
      <p>
        Upon termination of the Service, Veridex shall, at the Customer's choice, delete
        or return all Customer personal data within 30 days, and delete all existing copies
        unless storage is required by law.
      </p>

      <h2>10. Governing Law</h2>
      <p>
        This DPA is governed by the law of the Federal Republic of Germany.
      </p>

      <h2>11. Signing</h2>
      <p>
        By accepting the Terms of Service, the Customer is deemed to have signed this DPA
        in accordance with Art. 28(9) GDPR. A counter-signed version is available on
        request at <a href="mailto:legal@veridex-one.com">legal@veridex-one.com</a>.
      </p>
    </LegalLayout>
  );
}
